import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { conn } from '../net/connection';
import type { Session } from '../net/protocol';
import { errMsg, machineName, store, toast, useStore } from '../store';
import { fetchMe, logout, openPreview } from '../api';
import { getTerm, newSessionDims, peekTerm, setLeaveHandler, zoomSession } from '../term/manager';
import { telemetry } from '../net/telemetry';
import { setCreateSessionHook } from '../debug';
import * as T from './tabs';
import type { Page, TabsState } from './tabs';
import { Sidebar } from './Sidebar';
import { Palette } from './Palette';
import { Toasts } from './Toasts';
import { PaneToasts } from './PaneToasts';
import { adoptPageLinks, PAGE } from './openLinkStore';
import { Panes } from './Panes';
import { PageView } from './PageView';
import { TopBar } from './PaneBar';
import { ConnectionNotice } from './ConnectionNotice';
import { LoadBanner } from './LoadBanner';
import { OffersBanner } from './OffersBanner';
import { desktopProblem } from './desktopProblem';
import { installPermissionRequest, setNotificationClickHandler } from './notify';
import { isMac } from './keys';
import { recordingKeys } from './voiceKeys';
import { COARSE_MQ, COMPACT_MQ, installViewportTracking, useKeyboardOpen, useMedia } from './mobile';
import { KeyBar } from './KeyBar';
import { usePresence } from './ds';
import { SIDEBAR_MS, LAYOUT_SETTLED, reducedMotion } from './motion';
import { MobileTopBar } from './MobileBar';
import { TermSheet } from './TermSheet';
import { useVoiceKeys } from './VoiceCard';
import './workspace.css';
import { numberedSessions, orderedSessions } from './organize';
import { useWorkspaceSync } from './useWorkspaceSync';

export interface Actions {
  tabs: TabsState;
  /** The groups are known: the machine's heard (or this browser's own, with an agent that doesn't keep them). */
  ready: boolean;
  /** How many panes the group shown has. */
  paneCount: number;
  /** Show a session's row (its group as it was left); a session another row holds shows in its pane. */
  selectSession(id: number): void;
  /** Show the row holding session `id` with its pane and tab in front (a session's row inside a sidebar group). */
  revealSession(id: number): void;
  /** Session `id`'s row, dragged onto row `lead`'s in the sidebar: it joins that row's group as a new pane (tabs.ts joinRow). */
  joinRow(id: number, lead: number): void;
  /** Session `id`'s row in a group (Remove from split view, or dragged out of it): a row of its own again (tabs.ts leave). */
  unsplit(id: number): void;
  /** Show tab `k` of pane `pane`. */
  showTab(pane: number, k: number): void;
  closeTab(pane: number, k: number): void;
  /** Move tab `from` of pane `pane` to `to`. */
  moveTab(pane: number, from: number, to: number): void;
  /** Move tab `k` of pane `pane` into pane `to` at `at` (dragged onto another pane). */
  moveTabTo(pane: number, k: number, to: number, at: number): void;
  /** Move tab `k` of pane `pane` into a new pane split off pane `to` on `side` (dropped on that edge). */
  moveTabToSplit(pane: number, k: number, to: number, side: T.Side): void;
  /** A blank tab at the end of pane `pane` (+): it offers what to show; nothing starts until something is picked. */
  addBlank(pane: number): void;
  /** Put session `id` in place of the blank tab `blank`, or in the empty pane `i` (picked there), from whatever row
   *  held it. */
  fillPane(i: number, id: number, blank?: number): void;
  /** Show every pane of a zoomed group again (its Zoomed button: the machine's resize-pane -Z undone). */
  unzoom(): void;
  /** Split a pane (the focused one by default) right or down: a new pane beside or under it (up to 4). */
  split(dir?: 'row' | 'col', at?: number): void;
  closePane(i: number): void;
  focusPane(i: number): void;
  /** Resize the split at `path` in the layout tree. */
  resize(path: number[], sizes: number[]): void;
  resetSizes(path?: number[]): void;
  /** name: the person's (it stays); from: a session whose names it takes (a restart); cwd: its folder ("~/rel");
   *  pane: a tab in that pane of the group shown (an empty pane's New terminal), in place of its blank tab `blank`
   *  when there's one; else a row of its own. */
  newSession(opts?: { name?: string; command?: string; from?: number; cwd?: string; pane?: number; blank?: number }): Promise<Session | null>;
  /** New session (the sidebar's +, the palette): the start picker, as + opens it in a pane; a phone makes one at once. */
  startNew(): void;
  rename(id: number, name: string): Promise<void>;
  kill(id: number): Promise<void>;
  /** Brings back a recently closed session (Recently closed) as a new one, and opens it: in place of the blank tab
   *  `into.blank` of pane `into.pane` when it was picked there. */
  reopen(id: number, into?: { pane: number; blank?: number }): Promise<void>;
  restart(s: Session): Promise<void>;
  /** The pages: they take the place of the panes; picking a row goes back. */
  openDesktop(): void;
  openSettings(): void;
  openMachine(): void;
  /** The machine's files (Files). */
  openFiles(): void;
  startDesktop(): Promise<void>;
  stopDesktop(): Promise<void>;
  /** Open a port (or the desktop) in a browser tab of its own. */
  openExternal(port: number | 'desktop'): void;
  sidebarCollapsed: boolean;
  toggleSidebar(): void;
  openPalette(): void;
  logout(): Promise<void>;
  /** Phone layout (≤768 px): Home (the sessions) is the root page, a session or view is pushed over it, single pane. */
  compact: boolean;
  /** Phone: pop back to Home. */
  goHome(): void;
  /** `cmx detach` typed in session `id` here: back to the list (Home on a phone; elsewhere its tab closes). */
  leave(id: number): void;
}

/** ⌘= and ⌘+ (Shift and =), ⌘− and ⌘0, and the keypad's: a step bigger, smaller, or back. */
const ZOOM_KEYS: Record<string, -1 | 0 | 1> = { Equal: 1, NumpadAdd: 1, Minus: -1, NumpadSubtract: -1, Digit0: 0, Numpad0: 0 };

const Ctx = createContext<Actions | null>(null);
export const useActions = () => useContext(Ctx)!;

/** The panes' width over their height (the shape a new pane splits to fit; tabs.ts joinRow), when they show. */
export function panesAspect(): number | undefined {
  const r = document.querySelector('.ws-panes')?.getBoundingClientRect();
  return r && r.height ? r.width / r.height : undefined;
}

/** Where the keyboard is: in the panes, or nowhere in particular (what the machine's news may move it from). */
function focusIsOnPanes() {
  const ae = document.activeElement;
  return !ae || ae === document.body || !!ae.closest('.ws-panes');
}

/**
 * Calls `f(elapsed)` once session `sid`'s terminal is in a pane and attached — the end of the
 * "bring a session to the front" action (session.attach). A session already on screen resolves on
 * the next frame; a new one waits for its pane to mount it. `limitMs` only bounds the watcher: a
 * session that still isn't on screen then (a hidden tab, or one killed first) is an attach with
 * nothing to time, so `f(null)` says so and the caller records nothing rather than a failure. A
 * slow attach is a slow attach, not a failed one.
 */
function whenOnScreen(sid: number, f: (ms: number | null) => void, limitMs = 60_000): () => void {
  const t0 = performance.now();
  let raf = 0;
  const step = () => {
    const t = peekTerm(sid);
    if (t?.attached && t.onScreen) return f(performance.now() - t0);
    if (performance.now() - t0 >= limitMs) return f(null);
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}

function loadBool(key: string, dflt: boolean) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? dflt : v === '1';
  } catch {
    return dflt;
  }
}

export function Workspace() {
  const sessions = useStore((s) => s.sessions);
  const sessionsLoaded = useStore((s) => s.sessionsLoaded);
  const compact = useMedia(COMPACT_MQ);
  const coarse = useMedia(COARSE_MQ);
  const kbOpen = useKeyboardOpen();
  const [saved] = useState(() => T.loadTabs(store.get().me?.machine?.id));
  const [tabs, setTabs] = useState<TabsState>(saved.tabs);
  // the groups are the machine's (useWorkspaceSync); a render of its news animates and leaves the focus where it is
  const sync = useWorkspaceSync(saved, tabs, setTabs);
  const remote = tabs === sync.fromMachine.current;
  const onMachine = sync.mode === 'machine';
  // what this browser kept shows at once; kept on the machine, the panes wait for it (a round trip), the sidebar
  // grouped meanwhile as the machine last had them
  const ready = sync.mode !== 'waiting' || (!saved.machine && saved.tabs.groups.length > 0);
  const [collapsed, setCollapsed] = useState(() => loadBool('cmx.sidebarCollapsed', false));
  // Hiding and showing slide the sidebar out with its column (workspace.css). It stays mounted while it
  // leaves; terminals hold their size while it moves and fit once it settles.
  const sb = usePresence(!collapsed, SIDEBAR_MS);
  const [sbMoving, setSbMoving] = useState(false);
  const sbFirst = useRef(true);
  useEffect(() => {
    if (sbFirst.current) return void (sbFirst.current = false);
    if (reducedMotion()) return void window.dispatchEvent(new Event(LAYOUT_SETTLED));
    setSbMoving(true);
    const t = setTimeout(() => {
      setSbMoving(false);
      window.dispatchEvent(new Event(LAYOUT_SETTLED));
    }, SIDEBAR_MS + 40); // the entrance waits two frames before it starts
    return () => clearTimeout(t);
  }, [collapsed]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Phone navigation, as the iOS app's NavigationStack: Home at the root, a screen pushed over it.
  // The push is a history entry, so the browser's back button and the Android back gesture pop it,
  // and a reload keeps it (history.state survives); a fresh visit lands on Home.
  const [pushed, setPushed] = useState(() => !!history.state?.cmxPushed);
  const pushedRef = useRef(pushed);
  pushedRef.current = pushed;
  const [termFocused, setTermFocused] = useState(false);
  const compactRef = useRef(compact);
  compactRef.current = compact;
  const atHome = compact && !pushed;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const ids = useMemo(() => sessions.map((s) => s.id), [sessions]);
  const idsRef = useRef(ids);
  idsRef.current = ids;

  useEffect(() => {
    conn.start();
    installPermissionRequest();
    // Machine status can change under us (e.g. VM resume); refresh it periodically.
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchMe().then((r) => r.ok && store.set({ me: r.me }));
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  // Size the workspace to the visual viewport (on-screen keyboard) — see ui/mobile.ts.
  useEffect(() => installViewportTracking(), []);

  // The row shown and the page are this browser's (the groups too, with an agent that doesn't keep them).
  useEffect(() => {
    if (sync.mode !== 'waiting') T.saveTabs(tabs, onMachine);
  }, [tabs, sync.mode, onMachine]);

  // The browser's back (and the Android gesture) pops to Home.
  useEffect(() => {
    const onPop = () => setPushed(!!history.state?.cmxPushed);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const pushScreen = useCallback(() => {
    if (!compactRef.current || pushedRef.current) return;
    try {
      history.pushState({ cmxPushed: 1 }, '', location.href);
    } catch {
      /* no history: the in-app back still works */
    }
    pushedRef.current = true;
    setPushed(true);
  }, []);
  const goHome = useCallback(() => {
    if (!pushedRef.current) return;
    if (history.state?.cmxPushed) history.back(); // popstate does the rest
    else {
      pushedRef.current = false;
      setPushed(false);
    }
  }, []);

  // Arriving on Home: hide the keyboard, and bring the current session's row into view.
  useEffect(() => {
    if (!atHome) return;
    const ae = document.activeElement;
    if (ae instanceof HTMLElement && ae.classList.contains('xterm-helper-textarea')) ae.blur();
    const r = requestAnimationFrame(() => document.querySelector('.phone-home .session-row.active')?.scrollIntoView({ block: 'nearest' }));
    return () => cancelAnimationFrame(r);
  }, [atHome]);

  // Which element has focus decides whether the touch key bar shows.
  useEffect(() => {
    const isTerm = (el: EventTarget | null) => el instanceof HTMLElement && el.classList.contains('xterm-helper-textarea');
    const onIn = (e: FocusEvent) => setTermFocused(isTerm(e.target));
    const onOut = (e: FocusEvent) => {
      if (!isTerm(e.relatedTarget)) setTermFocused(false);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);

  // Tabs of sessions that ended close (the machine's groups say so themselves; its arrangement kept here, shown until
  // it speaks, leaves them out meanwhile). The first visit (no row shown yet) shows the first row.
  const firstRun = useRef(saved.machine || !tabs.groups.length);
  useEffect(() => {
    if (sessionsLoaded && !ready && saved.machine) setTabs((t) => T.reconcile(t, ids));
    if (!sessionsLoaded || !ready) return;
    // the ref is read here, not in the updater: updaters can run twice
    const first = firstRun.current ? orderedSessions(store.get().sessions)[0] : undefined;
    firstRun.current = false;
    setTabs((t) => {
      const next = onMachine ? t : T.reconcile(t, ids);
      return first && next.at === null && !next.page ? T.select(next, first.id) : next;
    });
  }, [ids, sessionsLoaded, ready, onMachine]);

  // Publish which session has focus (drives ping cadence + notification suppression).
  // On a phone's Home no session is on screen.
  const focusedSession = atHome ? null : T.focusedSession(tabs);
  const focusedPane = T.current(tabs)?.focused;
  // Touch key bar for the Desktop page: shown while it's in front and this tab drives it.
  const desktopControlling = useStore((s) => s.desktopControlling && s.desktopRunning);
  const desktopKeys = tabs.page === 'desktop' && desktopControlling;
  const lastFocused = useRef<number | null>(null);
  useEffect(() => {
    store.set({ focusedSession });
    if (focusedSession !== null) adoptPageLinks(focusedSession);
    const moved = focusedSession !== lastFocused.current;
    lastFocused.current = focusedSession;
    // The machine's news (an agent's split) takes the focus only when the machine moved it, and never from outside the
    // panes (the palette, a dialog, the sidebar): as tmux, a split without -d focuses the new pane.
    if (remote && (!moved || !focusIsOnPanes())) return;
    // Touch devices: don't pop the on-screen keyboard on every switch; a tap on the terminal focuses it.
    if (focusedSession !== null && !paletteOpen && !coarse) {
      // Defer so the pane has mounted its terminal.
      const r = requestAnimationFrame(() => peekTerm(focusedSession)?.focus());
      return () => cancelAnimationFrame(r);
    }
  }, [focusedSession, focusedPane, paletteOpen, coarse]);

  const openPage = useCallback(
    (p: Page) => {
      pushScreen();
      setTabs((t) => T.openPage(t, p));
    },
    [pushScreen],
  );
  const selectSession = useCallback(
    (id: number) => {
      pushScreen();
      setTabs((t) => T.select(t, id));
      // The row, the palette and a notification all come through here. Until its pane has mounted
      // the term and the session is on screen (its first paint isn't observable from here). A
      // hidden tab can't show it at all, so there's nothing to time and no failure to report.
      if (document.visibilityState === 'visible') {
        whenOnScreen(id, (ms) => {
          if (ms !== null) telemetry.recordOp('session.attach', ms);
        });
      }
    },
    [pushScreen],
  );
  /** A session just started or reopened: listed at once (before the machine's list says so), shown: in place of the
   *  blank tab `blank`, or a tab in `pane` of the group shown, else a row of its own. */
  const open = useCallback(
    (s: Session, pane?: number, blank?: number) => {
      store.set((st) => (st.sessions.some((x) => x.id === s.id) ? {} : { sessions: [...st.sessions, s] }));
      getTerm(s.id);
      pushScreen();
      setTabs((t) =>
        pane !== undefined && blank !== undefined ? T.fillPane(t, pane, s.id, blank) : pane !== undefined && !compactRef.current ? T.addTab(t, s.id, pane) : T.select(t, s.id),
      );
    },
    [pushScreen],
  );

  const actions = useMemo<Actions>(() => {
    const a: Actions = {
      tabs,
      ready,
      paneCount: T.current(tabs)?.panes.length ?? 0,
      selectSession,
      revealSession: (id) => {
        pushScreen();
        setTabs((t) => T.select(t, id, true));
      },
      joinRow: (id, lead) => {
        const aspect = panesAspect();
        setTabs((t) => T.joinRow(t, id, lead, aspect));
      },
      unsplit: (id) => setTabs((t) => T.leave(t, id)),
      showTab: (i, k) => setTabs((t) => T.activate(t, i, k)),
      closeTab: (i, k) => setTabs((t) => T.closeTab(t, i, k)),
      moveTab: (i, from, to) => setTabs((t) => T.moveTab(t, i, from, to)),
      moveTabTo: (i, k, j, at) => setTabs((t) => T.moveTabTo(t, i, k, j, at)),
      moveTabToSplit: (i, k, j, side) => setTabs((t) => T.moveTabToSplit(t, i, k, j, side)),
      addBlank: (i) => setTabs((t) => T.addBlank(t, i)),
      fillPane: (i, id, blank) => setTabs((t) => T.fillPane(t, i, id, blank)),
      // also a click handler: anything but 'col' and a pane index means the focused pane, split right
      split: (dir, at) => setTabs((t) => T.split(t, dir === 'col' ? 'col' : 'row', typeof at === 'number' ? at : undefined)),
      closePane: (i) => setTabs((t) => T.closePane(t, i)),
      unzoom: () => setTabs((t) => T.unzoom(t)),
      leave: (id) => (compact ? goHome() : setTabs((t) => T.leave(t, id))),
      focusPane: (i) => setTabs((t) => T.focus(t, i)),
      resize: (path, sizes) => setTabs((t) => T.resize(t, path, sizes)),
      resetSizes: (path) => setTabs((t) => T.resetSizes(t, Array.isArray(path) ? path : undefined)),
      async newSession(opts) {
        const { cols, rows } = newSessionDims();
        const { pane, blank, ...args } = opts ?? {};
        const t0 = performance.now();
        try {
          const r = await conn.control<{ session: Session }>('create', { ...args, cols, rows });
          // session.create: this action, until the new session id is known.
          telemetry.recordOp('session.create', performance.now() - t0);
          // session.prompt: the same action, until the new session's first output is painted
          // (registered before it is shown, so the attach's first draw is caught).
          getTerm(r.session.id).afterPaint(() => telemetry.recordOp('session.prompt', performance.now() - t0));
          open(r.session, pane, blank);
          return r.session;
        } catch (e) {
          telemetry.recordOp('session.create', performance.now() - t0, false);
          toast({ title: `Couldn’t create a session on ${machineName()}`, body: errMsg(e), kind: 'error' });
          return null;
        }
      },
      startNew: () => (compact ? void a.newSession() : setTabs((t) => T.showStart(t))),
      async reopen(id, into) {
        try {
          const r = await conn.control<{ session: Session }>('reopen', { session: id });
          store.set((st) => ({ closed: st.closed.filter((c) => c.id !== id) }));
          open(r.session, into?.blank !== undefined ? into.pane : undefined, into?.blank);
        } catch (e) {
          toast({ title: 'Couldn’t reopen the session', body: errMsg(e), kind: 'error' });
        }
      },
      async rename(id, name) {
        try {
          const r = await conn.control<{ session: Session }>('rename', { session: id, name });
          if (r.session) store.set((st) => ({ sessions: st.sessions.map((x) => (x.id === id ? r.session : x)) }));
        } catch (e) {
          toast({ title: 'Couldn’t rename the session', body: errMsg(e), kind: 'error' });
        }
      },
      async kill(id) {
        try {
          await conn.control('kill', { session: id });
        } catch (e) {
          toast({ title: 'Couldn’t end the session', body: errMsg(e), kind: 'error' });
        }
      },
      async restart(s) {
        const created = await a.newSession({ from: s.id, command: s.command || undefined }); // its names come along
        if (!created) return;
        // Put the new session where the old one was, then remove the dead one.
        setTabs((t) => T.replace(t, s.id, created.id));
        await conn.control('kill', { session: s.id }).catch(() => {});
      },
      // the page only: the desktop's stream is off until it's asked for (Start), not started by a look at the page
      openDesktop: () => openPage('desktop'),
      openSettings: () => openPage('settings'),
      openMachine: () => openPage('machine'),
      openFiles: () => openPage('files'),
      async startDesktop() {
        store.set({ desktopBusy: true, desktopError: null });
        try {
          const r = await conn.control<{ running: boolean }>('desktop.start', {}, 60_000);
          store.set({ desktopRunning: r.running !== false });
        } catch (e) {
          store.set({ desktopError: errMsg(e) });
          toast({ title: `Couldn’t start ${machineName()}’s desktop`, body: desktopProblem(errMsg(e)), kind: 'error' });
        } finally {
          store.set({ desktopBusy: false });
        }
      },
      async stopDesktop() {
        store.set({ desktopBusy: true, desktopError: null });
        try {
          const r = await conn.control<{ running: boolean }>('desktop.stop', {}, 30_000);
          store.set({ desktopRunning: !!r.running });
        } catch (e) {
          toast({ title: `Couldn’t stop ${machineName()}’s desktop`, body: errMsg(e), kind: 'error' });
        } finally {
          store.set({ desktopBusy: false });
        }
      },
      openExternal: openPreview,
      sidebarCollapsed: collapsed,
      toggleSidebar: () =>
        setCollapsed((c) => {
          try {
            localStorage.setItem('cmx.sidebarCollapsed', c ? '0' : '1');
          } catch {
            /* ignore */
          }
          return !c;
        }),
      openPalette: () => setPaletteOpen(true),
      compact,
      goHome,
      async logout() {
        conn.stop();
        await logout();
        location.assign('/');
      },
    };
    return a;
  }, [tabs, ready, openPage, selectSession, open, compact, collapsed, goHome, pushScreen]);

  const actionsRef = useRef(actions);
  // A sign-in the desktop came up for is through: put the layout back.
  actionsRef.current = actions;

  useEffect(() => setNotificationClickHandler((sid) => actionsRef.current.selectSession(sid)), []);
  useEffect(() => setLeaveHandler((sid) => actionsRef.current.leave(sid)), []);

  // Links in from the dashboard's Activity: /app?session=3 shows that session, /app?desktop the
  // desktop. Taken once the session list is in, then dropped from the address.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (!sessionsLoaded || !(q.has('session') || q.has('desktop'))) return;
    history.replaceState(history.state, '', location.pathname);
    const sid = Number(q.get('session'));
    if (q.has('desktop')) actionsRef.current.openDesktop();
    else if (store.get().sessions.some((s) => s.id === sid)) actionsRef.current.selectSession(sid);
  }, [sessionsLoaded]);

  useEffect(() => setCreateSessionHook(async (command) => (await actionsRef.current.newSession({ command }))?.id ?? null), []);

  // Hold ⌥ Space to talk into the focused session.
  useVoiceKeys();

  // Global shortcuts (capture phase so they win over the focused terminal).
  useEffect(() => {
    const mac = isMac();
    const onKey = (e: KeyboardEvent) => {
      // a key the voice shortcuts took (useVoiceKeys listens first): a voice key chosen in Settings wins over these
      if (e.type !== 'keydown' || e.defaultPrevented) return;
      // Settings › Voice is recording a shortcut: every key is its to see, ⌘K included
      if (recordingKeys()) return;
      const k = e.key.toLowerCase();
      const paletteKey = k === 'k' && !e.shiftKey && !e.altKey && (e.metaKey || (!mac && e.ctrlKey));
      if (paletteKey) {
        e.preventDefault();
        e.stopPropagation();
        setPaletteOpen((o) => !o);
        return;
      }
      const numMod = mac ? e.metaKey && !e.ctrlKey && !e.altKey : e.altKey && !e.ctrlKey && !e.metaKey;
      if (numMod && !e.shiftKey && /^Digit[1-9]$/.test(e.code)) {
        const n = Number(e.code.slice(5)) - 1;
        const s = numberedSessions(store.get().sessions, tabsRef.current)[n];
        if (s) {
          e.preventDefault();
          e.stopPropagation();
          actionsRef.current.selectSession(s.id);
        }
        return;
      }
      // ⌘+ / ⌘− / ⌘0 (Ctrl off a Mac): the focused pane's text only, never the page's zoom; on a page (Desktop, Files,
      // Settings) the browser zooms as usual
      const zoomMod = mac ? e.metaKey && !e.ctrlKey && !e.altKey : e.ctrlKey && !e.metaKey && !e.altKey;
      const by = ZOOM_KEYS[e.code];
      const zoomed = zoomMod && by !== undefined && !tabsRef.current.page ? T.focusedSession(tabsRef.current) : null;
      if (zoomed !== null && by !== undefined) {
        e.preventDefault();
        e.stopPropagation();
        zoomSession(zoomed, by);
        return;
      }
      if (mac && e.metaKey && !e.shiftKey && !e.altKey && k === 'b') {
        e.preventDefault();
        e.stopPropagation();
        actionsRef.current.toggleSidebar();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // Render from the reconciled tabs so a pane never outlives its (killed) session for a frame.
  const effective = sessionsLoaded ? T.reconcile(tabs, ids) : tabs;
  // A pushed screen whose session has ended (or a reload into one that's gone) pops back to Home, once the groups are
  // known (kept on the machine, they come a round trip after the page).
  const shownView = effective.page ?? (T.current(effective) ? true : null);
  useEffect(() => {
    if (compact && pushed && sessionsLoaded && ready && !shownView) goHome();
  }, [compact, pushed, sessionsLoaded, ready, shownView, goHome]);
  const cls = ['app', compact ? 'is-compact' : collapsed || !sb.shown ? 'sidebar-collapsed' : '', coarse ? 'is-touch' : '', kbOpen ? 'kb-open' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <Ctx.Provider value={actions}>
      <div className={cls} data-testid="workspace" data-sb-moving={sbMoving && !compact ? '' : undefined} data-compact={compact ? '1' : '0'} data-screen={compact ? (pushed ? 'pushed' : 'home') : undefined}>
        {compact ? (
          <>
            <MobileTopBar atHome={atHome} />
            <div className="phone-home" data-testid="phone-home" inert={!atHome} id="workspace-home">
              <Sidebar variant="drawer" />
              {atHome && <PaneToasts sid={PAGE} />}
            </div>
          </>
        ) : (
          sb.mounted && <Sidebar />
        )}
        <div className="ws-main" inert={atHome}>
          {!compact && <TopBar page={effective.page} lead={T.current(effective)?.lead} />}
          {!compact && <LoadBanner />}
          {!compact && <OffersBanner />}
          {effective.page ? <PageView page={effective.page} /> : <Panes state={effective} compact={compact} glide={remote} />}
          <ConnectionNotice />
        </div>
        {coarse && termFocused && focusedSession !== null && <KeyBar />}
        {coarse && desktopKeys && !atHome && <KeyBar target="desktop" />}
        {paletteOpen && (
          <Palette
            onClose={() => {
              setPaletteOpen(false);
            }}
          />
        )}
        <TermSheet />
        <Toasts onSession={actions.selectSession} />
      </div>
    </Ctx.Provider>
  );
}
